import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, LayoutDashboard, TrendingUp, Command } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Prominent page-navigation card meant to sit next to KPIs in the header
 *  strip.  Two variants: `player-tracker` shows a stack of player avatars,
 *  `trending` shows a mini up-and-to-the-right sparkline. */
export default function PageNavCard({ variant, active, to, players = [] }) {
  const isTracker = variant === "player-tracker";
  const isCoach = variant === "coach";
  const label = isTracker ? "Player Tracker Dashboard"
              : isCoach   ? "Coach's Dashboard"
              :             "Trending Leaderboard";
  const subtitle = isTracker ? "Sortable player database"
                 : isCoach   ? "Touchpoints · Tasks · Trends"
                 :             "Last 5 vs season · Top movers";
  const Icon = isTracker ? LayoutDashboard
             : isCoach   ? Command
             :             TrendingUp;

  const inner = (
    <div className="flex items-center gap-3 h-full">
      <div
        className={`w-11 h-11 border flex items-center justify-center shrink-0 ${
          active
            ? "border-[var(--org-accent)] bg-[var(--org-accent)]/15 text-[var(--org-accent)]"
            : "border-white/10 text-[#a0a0a0]"
        }`}
        style={{ transition: "border-color 0.2s ease, color 0.2s ease" }}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className={`text-[9px] uppercase tracking-[0.3em] mb-0.5 ${active ? "text-[var(--org-accent)]" : "text-[#757575]"}`}>
          {active ? "Now Viewing" : "View"}
        </div>
        <div className="font-heading text-[15px] leading-tight text-white uppercase tracking-tight whitespace-nowrap">
          {label}
        </div>
        <div className="text-[10px] text-[#a0a0a0] mt-0.5 whitespace-nowrap">{subtitle}</div>
      </div>
      <div className="shrink-0 self-stretch flex items-center gap-2">
        {isTracker ? <PlayerAvatars players={players} /> : isCoach ? null : <SparklineViz active={active} />}
        {!active && <ArrowRight className="w-4 h-4 text-[#757575] group-hover:text-[var(--org-accent)]" style={{ transition: "color 0.2s ease" }} />}
      </div>
    </div>
  );

  if (active) {
    return (
      <div
        className="group relative w-full md:w-[340px] p-4 border-2 border-[var(--org-accent)] bg-[var(--org-accent)]/[0.05]"
        data-testid={`page-nav-${variant}-active`}
        aria-current="page"
      >
        {/* Small corner accent tags */}
        <div className="absolute -top-px -left-px w-2 h-2 bg-[var(--org-accent)]" />
        <div className="absolute -top-px -right-px w-2 h-2 bg-[var(--org-accent)]" />
        {inner}
      </div>
    );
  }
  return (
    <Link
      to={to}
      className="group w-full md:w-[340px] p-4 border border-white/10 bg-[#1c1c1c] hover:border-[var(--org-accent)] hover:bg-[#2f2f2f]"
      style={{ transition: "border-color 0.2s ease, background-color 0.2s ease" }}
      data-testid={`page-nav-${variant}`}
    >
      {inner}
    </Link>
  );
}

function PlayerAvatars({ players }) {
  // Pick the first 3-4 with a resolvable headshot for a lively stack.
  const withPhoto = (players || []).filter(p => p.has_uploaded_headshot || p.nba_official_id).slice(0, 4);
  const list = withPhoto.length ? withPhoto : (players || []).slice(0, 4);
  if (!list.length) return <div className="w-14 h-8" />;
  return (
    <div className="flex -space-x-2" aria-hidden="true">
      {list.map((p) => {
        const url = (p.has_uploaded_headshot || p.nba_official_id)
          ? `${BACKEND_URL}/api/players/${p.id}/headshot`
          : null;
        const init = (p.name || "?").split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase();
        return (
          <div key={p.id} className="w-8 h-8 border-2 border-[#1c1c1c] bg-[#101010] overflow-hidden rounded-full flex items-center justify-center">
            {url
              ? <img src={url} alt="" className="w-full h-full object-cover object-top" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              : <span className="text-[9px] font-bold text-[var(--org-accent)]">{init}</span>}
          </div>
        );
      })}
    </div>
  );
}

function SparklineViz({ active }) {
  // Simple up-and-to-the-right trend line — pure SVG, respects the org accent
  const color = active ? "var(--org-accent)" : "#4ade80";
  return (
    <svg width="64" height="32" viewBox="0 0 64 32" fill="none" aria-hidden="true">
      <polyline
        points="2,26 12,22 22,24 32,18 42,12 52,10 62,4"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <circle cx="62" cy="4" r="2.5" fill={color} />
    </svg>
  );
}
